// Use conditional URL based on environment
const apiURLroot =
  process.env.NODE_ENV === "development"
    ? "http://localhost:7071/api/"
    : "https://relmed.azurewebsites.net/api/";
// Previous hardcoded URL:
// const apiURLroot = "https://relmed.azurewebsites.net/api/";

// Local storage key for cached failed uploads
const FAILED_UPLOADS_KEY = "relmed_failed_uploads";
// Lock for processFailedUploads
let isProcessingUploads = false;

// Save failed upload to localStorage
function cacheFailedUpload(endpoint, data, participantId, method) {
  // Changed accessToken to participantId
  try {
    // Get existing cached uploads
    const existingUploads = JSON.parse(
      localStorage.getItem(FAILED_UPLOADS_KEY) || "[]"
    );

    // Add new failed upload with timestamp
    existingUploads.push({
      method,
      endpoint,
      data,
      participantId, // Store participantId
      timestamp: Date.now(),
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    });

    // Save back to localStorage
    localStorage.setItem(FAILED_UPLOADS_KEY, JSON.stringify(existingUploads));

    console.log(`Upload cached for later retry: ${endpoint}`);
    return true;
  } catch (error) {
    console.error("Failed to cache upload:", error);
    return false;
  }
}

// Remove a successful upload from cache
function removeFromCache(uploadId) {
  try {
    const existingUploads = JSON.parse(
      localStorage.getItem(FAILED_UPLOADS_KEY) || "[]"
    );
    const filteredUploads = existingUploads.filter(
      (upload) => upload.id !== uploadId
    );
    localStorage.setItem(FAILED_UPLOADS_KEY, JSON.stringify(filteredUploads));
  } catch (error) {
    console.error("Failed to remove upload from cache:", error);
  }
}

// Process all cached uploads
async function processFailedUploads(accessToken) {
  if (!accessToken) {
    console.log("No access token provided for retry processing");
    return;
  }

  // Decode token to get current user ID
  let currentUserId;
  try {
    const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));
    currentUserId = tokenPayload.oid || tokenPayload.sub;
  } catch (error) {
    console.error("Failed to decode access token for user validation:", error);
    return;
  }

  if (!currentUserId) {
    console.error("Could not extract user ID from access token");
    return;
  }

  const cachedUploads = JSON.parse(
    localStorage.getItem("cachedUploads") || "[]"
  );
  if (cachedUploads.length === 0) return;

  console.log(`Found ${cachedUploads.length} cached uploads to retry`);

  // Process each upload with user validation
  const retryPromises = cachedUploads.map(async (upload) => {
    try {
      // SECURITY CHECK: Validate cached data belongs to current user
      if (upload.data && upload.data.id && upload.data.id !== currentUserId) {
        console.warn(`Skipping cross-user retry: cached data for user ${upload.data.id}, current token for ${currentUserId}`);
        // LEAVE in cache for original user to retry when they log back in
        return;
      }

      // Safe to retry with current token
      if (upload.method === "POST") {
        await postData({
          endpoint: upload.endpoint,
          data: upload.data,
          accessToken,
          participantId: upload.participantId,
          fromCache: true,
          uploadId: upload.id,
        });
      } else if (upload.method === "PATCH") {
        await patchData({
          endpoint: upload.endpoint,
          data: upload.data,
          accessToken,
          fromCache: true,
          uploadId: upload.id,
        });
      }
    } catch (error) {
      console.error(`Failed to retry upload ${upload.id}:`, error);
    }
  });

  // Wait for all retries to complete
  await Promise.all(retryPromises);
}

async function postData({
  endpoint = "",
  data = {},
  timeout = 30000,
  accessToken = "",
  participantId = null,
  retries = 1,
  fromCache = false,
  uploadId = null,
}) {
  var response;
  while (retries > 0 && !(response && response.ok)) {
    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      const headers = {
        "Content-Type": "application/json",
      };
      if (accessToken) headers["Authorization"] = "Bearer " + accessToken;
      response = await fetch(
        endpoint.startsWith("https://") ? endpoint : apiURLroot + endpoint,
        {
          method: "POST",
          mode: "cors",
          cache: "no-cache",
          credentials: "same-origin",
          headers: headers,
          redirect: "follow",
          referrerPolicy: "no-referrer",
          body: JSON.stringify(data),
          signal: controller.signal,
        }
      );
      clearTimeout(id);
    } catch (e) {
      console.log("Request error:", e);
      // Don't cache here - we'll do it in the else block if needed
    }
    retries--;
  }

  if (response && response.ok) {
    // If this was a cached upload that succeeded, remove it from cache
    if (fromCache && uploadId) {
      removeFromCache(uploadId);
    }
    return response.json();
  } else {
    // If this was a fresh upload (not a retry attempt), cache it for later
    if (!fromCache && participantId) {
      cacheFailedUpload(endpoint, data, participantId, "post");
    } else if (!fromCache && !participantId) {
      console.warn(
        "Failed upload not cached: participantId was not provided to postData."
      );
    }

    try {
      const errorData = await response.json();
      return Promise.reject(errorData);
    } catch (e) {
      return Promise.reject({
        status: response?.status || 0,
        message: response ? `Server error (${response.status}): ${response.statusText}` : "Request was cancelled",
      });
    }
  }
}


async function patchData({
  endpoint = "",
  data = {},
  timeout = 30000,
  accessToken = "",
  retries = 1,
  fromCache = false,
  uploadId = null,
}) {
  var response;
  while (retries > 0 && !(response && response.ok)) {
    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      const headers = {
        "Content-Type": "application/json",
      };
      if (accessToken) headers["Authorization"] = "Bearer " + accessToken;
      response = await fetch(apiURLroot + endpoint, {
        method: "PATCH",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: headers,
        redirect: "follow",
        referrerPolicy: "no-referrer",
        body: JSON.stringify(data),
        signal: controller.signal,
      });
      clearTimeout(id);
    } catch (e) {
      console.log("Request error:", e);
      // Don't cache here - we'll do it in the else block if needed
    }
    retries--;
  }
  if (response && response.ok) {
    // If this was a cached upload that succeeded, remove it from cache
    if (fromCache && uploadId) {
      removeFromCache(uploadId);
    }
    return response.json();
  } else {
    // If this was a fresh upload (not a retry attempt), cache it for later
    if (!fromCache && data.id) {
      cacheFailedUpload(endpoint, data, data.id, "patch");
    }

    try {
      const errorData = await response.json();
      return Promise.reject(errorData);
    } catch (e) {
      return Promise.reject({
        status: response?.status || 0,
        message: response ? `Server error (${response.status}): ${response.statusText}` : "Request was cancelled",
      });
    }
  }
}

async function getData({
  endpoint = "",
  parameters = {},
  timeout = 30000,
  accessToken = "",
  retries = 1,
}) {
  var response;
  while (retries > 0 && !(response && response.ok)) {
    let queryString = "";
    try {
      queryString = Object.keys(parameters).reduce(
        (qs, key) => qs + (qs === "" ? "?" : "&") + key + "=" + parameters[key],
        ""
      );
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);
      const headers = {
        "Content-Type": "application/json",
      };
      if (accessToken) headers["Authorization"] = "Bearer " + accessToken;
      response = await fetch(apiURLroot + endpoint + queryString, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: headers,
        redirect: "follow",
        referrerPolicy: "no-referrer",
        signal: controller.signal,
      });
      clearTimeout(id);
    } catch (e) {
      console.log(e);
    }
    retries--;
  }
  if (response && response.ok) {
    return response.json();
  } else {
    try {
      const errorData = await response.json();
      return Promise.reject(errorData);
    } catch (e) {
      return Promise.reject({
        status: response.status,
        message: `Server error (${response.status}): ${response.statusText}`,
      });
    }
  }
}

// Export the functions
export {
  postData,
  patchData,
  getData,
  processFailedUploads,
  FAILED_UPLOADS_KEY,
}; // Export key for use in notification component
